import { useEffect, useMemo, useState } from "react";
import { cn } from "../../../app/components/ui/utils";
import { api } from "../../../lib/api";
import { useAuth } from "../../../providers/AuthProvider";
import { useDealerRecommendations, type DealerRecommendationRow } from "../hooks/useDealerRecommendations";

type DealerRow = { id: string; name: string; status: string | null };

function buildOrderedRows(dealers: DealerRow[], recoList: DealerRecommendationRow[] | undefined): Array<{ dealer: DealerRow; reco: DealerRecommendationRow | null; bestChoice: boolean }> {
  const recoById = new Map<string, DealerRecommendationRow>();
  for (const r of recoList ?? []) {
    recoById.set(String(r.dealer_id), r);
  }

  const withReco: DealerRow[] = [];
  const withoutReco: DealerRow[] = [];
  for (const d of dealers) {
    if (recoById.has(d.id)) {
      withReco.push(d);
    } else {
      withoutReco.push(d);
    }
  }

  withReco.sort((a, b) => {
    const sa = recoById.get(a.id)?.score ?? 0;
    const sb = recoById.get(b.id)?.score ?? 0;
    return sb - sa;
  });
  withoutReco.sort((a, b) => a.name.localeCompare(b.name));

  const ordered = [...withReco, ...withoutReco];
  return ordered.map((dealer, idx) => ({
    dealer,
    reco: recoById.get(dealer.id) ?? null,
    bestChoice: Boolean((recoList?.length ?? 0) > 0 && idx === 0 && recoById.has(dealer.id)),
  }));
}

export function DealerPickerPanel({
  value,
  onChange,
  items,
  lat,
  lng,
  disabled,
}: {
  value: string;
  onChange: (dealerId: string) => void;
  items: Array<{ product_id: string; quantity?: string }>;
  lat?: number | null;
  lng?: number | null;
  disabled?: boolean;
}) {
  const { token } = useAuth();
  const [dealers, setDealers] = useState<DealerRow[]>([]);
  const [listLoading, setListLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setListLoading(true);
      try {
        const res = await api.dealers.list({ token });
        if (!cancelled) {
          setDealers(res.data.map((d) => ({ id: d.id, name: d.name, status: d.status })));
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const hasProducts = useMemo(
    () => items.some((it) => String(it.product_id).trim() !== "" && Number(it.product_id) > 0),
    [items],
  );

  const recoQuery = useDealerRecommendations({ items, lat: lat ?? null, lng: lng ?? null });

  const rows = useMemo(() => {
    const sortedDealers = [...dealers].sort((a, b) => a.name.localeCompare(b.name));
    if (!hasProducts || recoQuery.isLoading || recoQuery.isError || !recoQuery.data) {
      return sortedDealers.map((dealer) => ({
        dealer,
        reco: null as DealerRecommendationRow | null,
        bestChoice: false,
      }));
    }
    return buildOrderedRows(sortedDealers, recoQuery.data);
  }, [dealers, hasProducts, recoQuery.isLoading, recoQuery.isError, recoQuery.data]);

  if (listLoading) {
    return (
      <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">Loading dealers…</div>
    );
  }

  if (!dealers.length) {
    return (
      <div className="rounded-md border border-dashed border-border/60 px-3 py-2 text-xs text-muted-foreground">No dealers yet — add one under Dealers.</div>
    );
  }

  return (
    <div className="space-y-2">
      {!hasProducts ? (
        <p className="text-xs text-muted-foreground leading-relaxed">Add products to line items for ranking and tips.</p>
      ) : recoQuery.isLoading ? (
        <p className="text-xs text-muted-foreground">Updating suggestions…</p>
      ) : recoQuery.isError ? (
        <p className="text-xs text-muted-foreground">Suggestions unavailable — choose any dealer.</p>
      ) : null}

      <div
        role="listbox"
        aria-label="Dealers"
        className="rounded-md border border-border/50 bg-background/80 max-h-[min(320px,45vh)] overflow-y-auto shadow-sm"
      >
        {rows.map(({ dealer, reco, bestChoice }) => {
          const selected = value !== "" && String(value) === String(dealer.id);
          return (
            <button
              key={dealer.id}
              type="button"
              role="option"
              aria-selected={selected}
              disabled={disabled}
              onClick={() => onChange(String(dealer.id))}
              className={cn(
                "w-full text-left border-b border-border/40 last:border-b-0 px-3 py-2 transition-colors",
                "hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-inset",
                selected && "bg-muted/25 border-l-2 border-l-primary pl-[10px]",
                !selected && "border-l-2 border-l-transparent pl-[10px]",
                disabled && "opacity-50 pointer-events-none",
              )}
            >
              <div className="flex items-start justify-between gap-3 min-h-[1.25rem]">
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-medium text-foreground leading-snug truncate">{dealer.name}</span>
                    {bestChoice ? (
                      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground shrink-0">Top pick</span>
                    ) : null}
                  </div>
                  {dealer.status && dealer.status !== "active" ? (
                    <span className="text-[11px] text-muted-foreground capitalize">{dealer.status}</span>
                  ) : null}
                </div>
                {reco ? (
                  <span className="text-[11px] tabular-nums text-muted-foreground shrink-0 pt-0.5">
                    {(reco.score * 100).toFixed(0)}
                  </span>
                ) : null}
              </div>

              {reco && (typeof reco.distance_km === "number" || (reco.insights?.length ?? 0) > 0) ? (
                <div className="mt-1 space-y-0.5 pl-0">
                  {typeof reco.distance_km === "number" ? (
                    <p className="text-[11px] text-muted-foreground tabular-nums">{reco.distance_km.toFixed(1)} km</p>
                  ) : null}
                  {Array.isArray(reco.insights) && reco.insights.length > 0 ? (
                    <p className="text-[11px] text-muted-foreground leading-snug line-clamp-2">
                      {reco.insights.slice(0, 2).join(" · ")}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
